import { HStack, PageContainer, PageHeader, Spread } from '@/components/Atoms'
import { Card, CardContent } from '@/components/Card'
import { Filter } from '@/components/DataTable/FilterControls'
import { ExportCsvButton } from '@/components/ExportCsv/ExportCsvButton'
import { Note } from '@/components/Note'
import useCreditLegalNameFilter from '@/hooks/filters/credit/useCreditLegalNameFilter'
import useCreditEquifaxCodeFilter from '@/hooks/filters/credit/useCreditEquifaxCodeFilter'
import useCreditScoreTierFilter from '@/hooks/filters/credit/useCreditScoreTierFilter'
import {
  creditIndexScoresExportQueryOptions,
  creditIndexScoresSearchSchema,
  useCreditIndexScores,
  useCreditIndexScoreYears,
} from '@/hooks/queries/credit/useCreditIndexScores'
import { useTablePagination } from '@/hooks/useTablePagination'
import { getClearedFilters } from '@/lib/utils/filters'
import { queryClient } from '@/main'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@repo/ui/components'
import { createFileRoute } from '@tanstack/react-router'
import { InfoIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CreditIndexScoresDataTable } from './-components/CreditIndexScoresDataTable'
import { ImportHistoryPanel } from './-components/ImportHistoryPanel'
import { UploadCreditIndexScoresDialog } from './-components/UploadCreditIndexScoresDialog'

export const Route = createFileRoute('/_auth/_modules/credit/credit-index-scores/')({
  component: RouteComponent,
  validateSearch: (search) => creditIndexScoresSearchSchema.parse(search),
})

const CURRENT_YEAR = new Date().getFullYear()

function RouteComponent() {
  const { t } = useTranslation(['credit', 'common'])
  const navigate = Route.useNavigate()
  const search = Route.useSearch()

  const { pageSize, onPageSizeChange } = useTablePagination({ navigate, search })

  // Only offer years that exist in the system (newest first); fall back to the current year.
  const { data: availableYears } = useCreditIndexScoreYears()
  const yearOptions = (availableYears?.length ? availableYears : [CURRENT_YEAR])
    .slice()
    .sort((a, b) => b - a)
  const defaultYear = yearOptions[0]!

  const selectedYear = search.year ?? defaultYear
  // Default view: with no year or history range chosen, the picker lands on "Last 12 months".
  const isLast12 =
    search.historyRange === 'last12' ||
    (search.year === undefined && search.historyRange === undefined)

  const { legalNameFilter, legalNames, setLegalNames } = useCreditLegalNameFilter(
    search.legalName,
    selectedYear,
  )
  const { equifaxCodeFilter, equifaxCodes, setEquifaxCodes } = useCreditEquifaxCodeFilter(
    search.equifaxCode,
  )
  const { scoreTierFilter, scoreTier, setScoreTier } = useCreditScoreTierFilter(search.scoreTier, {
    hasInvalidOption: true,
  })
  const [keyword, setKeyword] = useState<string>(search.keyword ?? '')
  // Imports are processed asynchronously by the worker; on success we show an info banner
  // telling the user to refresh their browser once processing has had a moment.
  const [importSucceeded, setImportSucceeded] = useState(false)

  const selectedFilters = {
    legalName: legalNames?.length ? legalNames : undefined,
    equifaxCode: equifaxCodes?.length ? equifaxCodes : undefined,
    scoreTier: scoreTier.size ? Array.from(scoreTier) : undefined,
  }

  const filters = [legalNameFilter, equifaxCodeFilter, scoreTierFilter]

  const creditIndexScoresQuery = useCreditIndexScores({
    year: selectedYear,
    historyRange: isLast12 ? 'last12' : undefined,
    keyword: search.keyword,
    legalName: search.legalName,
    equifaxCode: search.equifaxCode,
    scoreTier: search.scoreTier,
    page: search.page ?? 1,
    pageSize,
    sort: search.sort,
  })

  const LAST_12_VALUE = 'last12'

  const handleYearChange = (value: string) => {
    // Keep the page and row set; only the Score Trend window changes.
    if (value === LAST_12_VALUE) {
      navigate({ search: { ...search, historyRange: 'last12' } })
      return
    }
    navigate({ search: { ...search, year: Number(value), historyRange: undefined } })
  }

  const showResults = () => {
    navigate({
      search: {
        ...search,
        ...selectedFilters,
        keyword: keyword || undefined,
        page: 1,
      },
    })
  }

  const resetAllFilters = () => {
    setKeyword('')
    setLegalNames([])
    setEquifaxCodes([])
    setScoreTier(new Set())

    const clearedFilters = getClearedFilters(selectedFilters)

    navigate({
      search: {
        ...search,
        ...clearedFilters,
        keyword: undefined,
        page: 1,
      },
    })
  }

  const getCsvExport = () =>
    queryClient.ensureQueryData(
      creditIndexScoresExportQueryOptions({
        year: selectedYear,
        keyword: search.keyword,
        legalName: search.legalName,
        equifaxCode: search.equifaxCode,
        scoreTier: search.scoreTier,
      }),
    )

  return (
    <PageContainer maxWidth="wide">
      <Spread>
        <PageHeader>{t('creditIndexScores.pageHeader')}</PageHeader>
        <HStack gap="sm">
          <ImportHistoryPanel />

          <UploadCreditIndexScoresDialog onUploadSuccess={() => setImportSucceeded(true)} />

          <ExportCsvButton
            getCsvExport={getCsvExport}
            rowCount={creditIndexScoresQuery.data?.rowCount ?? 0}
            label={t('export.viewData', { ns: 'common' })}
          />

          <Select
            value={isLast12 ? LAST_12_VALUE : String(selectedYear)}
            onValueChange={handleYearChange}
          >
            <SelectTrigger className="bg-background w-[160px]">
              <SelectValue placeholder={t('creditIndexScores.selectYear')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={LAST_12_VALUE}>{t('creditIndexScores.last12Months')}</SelectItem>
              {yearOptions.map((year) => (
                <SelectItem key={year} value={String(year)}>
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </HStack>
      </Spread>

      {importSucceeded && (
        <Note tone="info" icon={InfoIcon} note={t('creditIndexScores.import.successBanner')} />
      )}

      <Card>
        <CardContent className="pt-6">
          <CreditIndexScoresDataTable
            year={selectedYear}
            filters={filters as Filter<unknown>[]}
            search={{
              value: keyword,
              setValue: setKeyword,
              placeholder: t('creditIndexScores.searchPlaceholder'),
            }}
            pageSize={pageSize}
            onPageSizeChange={onPageSizeChange}
            creditIndexScoresQuery={creditIndexScoresQuery}
            onShowResults={showResults}
            onResetAllFilters={resetAllFilters}
          />
        </CardContent>
      </Card>
    </PageContainer>
  )
}
